import React from 'react';
import { Calendar, Filter, RefreshCw } from 'lucide-react';
import { cn } from '../../../utils/tailwindUtils';


const PRESETS = [
  { value: 'all',        label: 'All time',    days: null },
  { value: 'today',      label: 'Today',       days: 0 },
  { value: 'last_7',     label: 'Last 7 days',  days: 7 },
  { value: 'last_30',    label: 'Last 30 days', days: 30 },
  { value: 'last_90',    label: 'Last 90 days', days: 90 },
  { value: 'this_month', label: 'This month',  days: null },
  { value: 'custom',     label: 'Custom',      days: null },
];


/** Convert a preset to {from, to} ISO date strings. Returns nulls for 'all'. */
export function presetToRange(preset, customFrom, customTo) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const back = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return iso(d);
  };
  if (preset === 'custom') return { from: customFrom || null, to: customTo || null };
  if (preset === 'all')    return { from: null, to: null };
  if (preset === 'today')  return { from: iso(today), to: iso(today) };
  if (preset === 'this_month') {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: iso(first), to: iso(today) };
  }
  const p = PRESETS.find(x => x.value === preset);
  if (p?.days != null) return { from: back(p.days), to: iso(today) };
  return { from: null, to: null };
}


/**
 * Filter bar for engage reports.
 *
 * Props:
 *   preset, setPreset            — date preset state
 *   customFrom, setCustomFrom    — custom start (used when preset='custom')
 *   customTo,   setCustomTo      — custom end
 *   extras                       — array of {label, value, onChange, options: [{value, label}]}
 *                                  Rendered as dropdowns next to the date filter.
 *   onReload                     — optional refresh callback
 */
export default function ReportFilters({
  preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo,
  extras = [], onReload,
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mr-1">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>

        {/* Date preset */}
        <div className="inline-flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 text-gray-400" />
          <select value={preset} onChange={e => setPreset(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            {PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>

        {preset === 'custom' && (
          <>
            <input type="date" value={customFrom || ''} onChange={e => setCustomFrom(e.target.value)}
              className="px-2 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span className="text-xs text-gray-500">→</span>
            <input type="date" value={customTo || ''} onChange={e => setCustomTo(e.target.value)}
              className="px-2 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </>
        )}

        {/* Extra dropdowns */}
        {extras.map((ex, i) => (
          <select key={i} value={ex.value} onChange={e => ex.onChange(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            {ex.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ))}

        {onReload && (
          <button onClick={onReload}
            className={cn('ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700')}>
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * Utility: filter an array of rows by created_date (or a custom date field)
 * against a preset range. Used by client-side reports.
 */
export function filterByRange(rows, preset, customFrom, customTo, dateField = 'created_date') {
  const { from, to } = presetToRange(preset, customFrom, customTo);
  if (!from && !to) return rows;
  const fromMs = from ? new Date(from + 'T00:00:00').getTime() : -Infinity;
  const toMs   = to   ? new Date(to   + 'T23:59:59').getTime() :  Infinity;
  return rows.filter(r => {
    const d = r[dateField];
    if (!d) return false;
    const ms = new Date(d).getTime();
    return ms >= fromMs && ms <= toMs;
  });
}
